/**
 * Tooltip — modern, fully-featured tooltip atom.
 *
 * Features
 * ─────────
 *  • 12 placement positions with automatic viewport flip
 *  • Smooth scale + slide CSS transition (enter & exit)
 *  • Dynamic arrow that tracks the real anchor centre
 *  • Scroll + resize repositioning via event listeners
 *  • Portal rendering (always above all stacking contexts)
 *  • Rich content: `content` accepts any ReactNode
 *  • 6 visual variants: default | light | info | success | warning | danger
 *  • `unstyled` escape-hatch for fully custom panels (e.g. image previews)
 *  • Hover + focus triggers; configurable show/hide delays
 *  • Controlled (`open` prop) and uncontrolled modes
 *  • Backward-compat with old atom API: `showToolTips`, `label`, `id`
 *  • `forwardRef` so Tooltips can be safely nested
 *
 * Usage
 * ─────
 *  // Basic
 *  <Tooltip content="Save changes" placement="top">
 *    <button>Save</button>
 *  </Tooltip>
 *
 *  // Controlled "Copied!" flash
 *  <Tooltip showToolTips={copied} label="Copied!" placement="top">
 *    <span onClick={handleCopy}>{id}</span>
 *  </Tooltip>
 *
 *  // Rich content + custom styling
 *  <Tooltip unstyled arrow={false} content={<ImagePreview />}
 *           className="p-0 rounded-xl border border-white/10 shadow-2xl">
 *    <img src={thumb} />
 *  </Tooltip>
 */

import React, {
    forwardRef,
    ReactNode,
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    useCallback,
    cloneElement,
    isValidElement,
} from 'react';
import { createPortal } from 'react-dom';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { Text } from '@/components/atoms/AppText';

// ─── Exported types ───────────────────────────────────────────────────────────

export type TooltipPlacement =
    | 'top' | 'top-start' | 'top-end'
    | 'bottom' | 'bottom-start' | 'bottom-end'
    | 'left' | 'left-start' | 'left-end'
    | 'right' | 'right-start' | 'right-end';

export type TooltipVariant =
    | 'default' | 'light' | 'info' | 'success' | 'warning' | 'danger';

export interface TooltipProps {
    /**
     * Content shown inside the tooltip — any ReactNode (string, JSX, icon+text…).
     * Also accepted as `label` (old atom-API alias).
     */
    content?: ReactNode;
    /** Alias for `content` — kept for backward compatibility with old atom API */
    label?: ReactNode;

    /**
     * Programmatic show / hide control (old atom-API shorthand).
     *
     *   `true`      → force-open the tooltip (controlled visible)
     *   `false`     → disable entirely; never shown even on hover
     *   `undefined` → normal hover / focus behaviour (default)
     */
    showToolTips?: boolean;

    /** Disable the tooltip; overrides every other open mechanism */
    disabled?: boolean;
    /** Fully-controlled open state — overrides hover / focus */
    open?: boolean;
    /** Uncontrolled initial open state (default: false) */
    defaultOpen?: boolean;

    /** Preferred placement relative to the anchor (default: 'top') */
    placement?: TooltipPlacement;

    /** Delay in ms before the tooltip appears (default: 0) */
    delayShow?: number;
    /** Delay in ms before the tooltip hides (default: 80) */
    delayHide?: number;

    /** Max-width of the tooltip panel — number = px, string = any CSS value (default: 280) */
    maxWidth?: number | string;

    /** Visual colour theme (default: 'default') */
    variant?: TooltipVariant;

    /**
     * Strip all built-in panel styles so `className` has full control.
     * Use this for fully custom-coloured tooltips (e.g. image-preview thumbnails,
     * Stepper step-detail callouts).
     */
    unstyled?: boolean;

    /** Show the directional arrow (default: true) */
    arrow?: boolean;

    /** Gap between the tooltip panel and the anchor in px (default: 8) */
    offset?: number;

    /** Extra Tailwind classes appended to the tooltip panel */
    className?: string;

    /** The trigger element — must be a single React element or plain children */
    children: ReactNode;

    /** Legacy prop — ignored; kept so old ToolTip usages don't need surgery */
    id?: string;

    /** Callback fired when the open state changes */
    onOpenChange?: (open: boolean) => void;

    /**
     * Labeled layout — renders a structured label + value inside the panel.
     * When set, `content` is ignored.
     *
     *   <Tooltip title="Provider Name" value={item.name}>
     *     {trigger}
     *   </Tooltip>
     */
    /** Small all-caps label line shown above `value` */
    title?: string;
    /** Primary value displayed below `title` */
    value?: ReactNode;
}

// ─── Variant palette ──────────────────────────────────────────────────────────
//
// Design language: rich glass-dark default, gradient tints for semantic variants.
// All panels use backdrop-blur in fixed-position portals for depth.

const PANEL_CLS: Record<TooltipVariant, string> = {
    default:
        `${APP_BG.tooltip} ${APP_TEXT['white-soft']} ` +
        `border border-white/[0.08] shadow-lg`,

    light:
        `${APP_BG.elevated} ` +
        `${APP_TEXT.soft} ` +
        `border ${APP_BORDER.card} shadow-md`,

    info:
        `bg-gradient-to-br from-blue-600 to-blue-500 ${APP_TEXT.white} ` +
        `border border-blue-400/25 shadow-md shadow-blue-900/30`,
    success:
        `bg-gradient-to-br from-emerald-600 to-emerald-500 ${APP_TEXT.white} ` +
        `border border-emerald-400/25 shadow-md shadow-emerald-900/30`,
    warning:
        `bg-gradient-to-br from-amber-500 to-amber-400 ${APP_TEXT.white} ` +
        `border border-amber-300/30 shadow-md shadow-amber-900/30`,
    danger:
        `bg-gradient-to-br from-red-600 to-rose-500 ${APP_TEXT.white} ` +
        `border border-red-400/25 shadow-md shadow-red-900/30`,
};

// Arrow shares the panel's starting colour — reuse APP_BG tokens to stay in sync
const ARROW_BG: Record<TooltipVariant, string> = {
    default: APP_BG.tooltip,   // bg-gray-900
    light: APP_BG.elevated,  // bg-white dark:bg-[#0c1220]
    info: 'bg-blue-600',
    success: 'bg-emerald-600',
    warning: 'bg-amber-500',
    danger: 'bg-red-600',
};

const ARROW_BORDER: Record<TooltipVariant, string> = {
    default: 'border-white/[0.08]',
    light: APP_BORDER.card,  // border-gray-200/80 dark:border-white/[0.07]
    info: 'border-blue-400/25',
    success: 'border-emerald-400/25',
    warning: 'border-amber-300/30',
    danger: 'border-red-400/25',
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

type PlacementBase = 'top' | 'bottom' | 'left' | 'right';
type PlacementAlign = 'start' | 'end' | undefined;

function parsePlacement(p: TooltipPlacement): { base: PlacementBase; align: PlacementAlign } {
    const [base, align] = p.split('-') as [PlacementBase, PlacementAlign];
    return { base, align };
}

interface AnchorRect {
    top: number; left: number; right: number; bottom: number;
    width: number; height: number;
}

/** Compute fixed-position {x, y} for the tooltip plus the resolved placement after any flip. */
function calcPosition(
    a: AnchorRect,
    tw: number, th: number,
    placement: TooltipPlacement,
    offset: number,
): { x: number; y: number; placement: TooltipPlacement } {
    const { base, align } = parsePlacement(placement);
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = 0, y = 0;

    // ── Primary axis ──────────────────────────────────────────────────────────
    switch (base) {
        case 'top': y = a.top - th - offset; break;
        case 'bottom': y = a.bottom + offset; break;
        case 'left': x = a.left - tw - offset; break;
        case 'right': x = a.right + offset; break;
    }

    // ── Cross axis alignment ──────────────────────────────────────────────────
    if (base === 'top' || base === 'bottom') {
        if (align === 'start') x = a.left;
        else if (align === 'end') x = a.right - tw;
        else x = a.left + a.width / 2 - tw / 2;
    } else {
        if (align === 'start') y = a.top;
        else if (align === 'end') y = a.bottom - th;
        else y = a.top + a.height / 2 - th / 2;
    }

    // ── Auto-flip when out of viewport ────────────────────────────────────────
    let finalPlacement = placement;
    if (base === 'top' && y < 4) { y = a.bottom + offset; finalPlacement = (align ? `bottom-${align}` : 'bottom') as TooltipPlacement; }
    else if (base === 'bottom' && y + th > vh - 4) { y = a.top - th - offset; finalPlacement = (align ? `top-${align}` : 'top') as TooltipPlacement; }
    else if (base === 'left' && x < 4) { x = a.right + offset; finalPlacement = (align ? `right-${align}` : 'right') as TooltipPlacement; }
    else if (base === 'right' && x + tw > vw - 4) { x = a.left - tw - offset; finalPlacement = (align ? `left-${align}` : 'left') as TooltipPlacement; }

    // ── Clamp to viewport (4 px margin) ───────────────────────────────────────
    x = Math.max(4, Math.min(x, vw - tw - 4));
    y = Math.max(4, Math.min(y, vh - th - 4));

    return { x, y, placement: finalPlacement };
}

/**
 * Which two sides of the 10×10 rotated square should show the panel border
 * so the protruding tip blends seamlessly into the panel edge.
 *
 *  placement base → the tip "points toward" the anchor →
 *    top    → tip faces down  → show border-right + border-bottom
 *    bottom → tip faces up    → show border-top  + border-left
 *    left   → tip faces right → show border-top  + border-right
 *    right  → tip faces left  → show border-bottom + border-left
 */
const ARROW_SIDES: Record<PlacementBase, string> = {
    top: 'border-r border-b',
    bottom: 'border-t border-l',
    left: 'border-t border-r',
    right: 'border-b border-l',
};

/** Entry/exit animation origin and slide offset per base placement */
const ANIM_ORIGIN: Record<PlacementBase, string> = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'right center',
    right: 'left center',
};
// Exit shift — slightly larger than before so the hide feels intentional
const ANIM_EXIT_SHIFT: Record<PlacementBase, string> = {
    top: 'translateY(7px)',
    bottom: 'translateY(-7px)',
    left: 'translateX(7px)',
    right: 'translateX(-7px)',
};

// ─── Component ────────────────────────────────────────────────────────────────

const Tooltip = forwardRef<Element, TooltipProps>(function Tooltip(
    {
        content,
        label,
        showToolTips,
        disabled = false,
        open: controlledOpen,
        defaultOpen = false,
        placement = 'top',
        delayShow = 0,
        delayHide = 80,
        maxWidth = 280,
        variant = 'default',
        unstyled = false,
        arrow = true,
        offset = 8,
        className = '',
        children,
        id: _legacyId,
        onOpenChange,
        title,
        value: labeledValue,
    },
    forwardedRef,
) {
    const resolvedContent = title
        ? (
            <span className="flex flex-col">
                {/* Label row */}
                <span className="flex items-center gap-1.5 pb-[3px] mb-[4px] border-b border-white/[0.08]">
                    <span className="w-[3px] h-[3px] rounded-full bg-[var(--color-primary)] flex-shrink-0" />
                    <Text
                        as="span"
                        size="2xs"
                        color="white-muted"
                        uppercase
                        tracking="widest"
                        leading="none"
                    >
                        {title}
                    </Text>
                </span>
                {/* Value row */}
                {typeof labeledValue === 'string' ? (
                    <Text as="span" size="base" color="white" leading="snug">
                        {labeledValue}
                    </Text>
                ) : (
                    labeledValue
                )}
            </span>
        )
        : (content ?? label);

    // ── Resolved control flags ────────────────────────────────────────────────
    const isDisabled = disabled || showToolTips === false;
    const isForcedOpen = showToolTips === true;
    const isControlled = controlledOpen !== undefined || isForcedOpen;
    const controlledVal = isForcedOpen ? true : (controlledOpen ?? false);

    // ── Refs ──────────────────────────────────────────────────────────────────
    const anchorRef = useRef<Element | null>(null);
    const tipRef = useRef<HTMLDivElement | null>(null);

    // ── State ─────────────────────────────────────────────────────────────────
    // `mounted`  — portal is in the DOM
    // `visible`  — CSS opacity/transform transition is "in"
    const [mounted, setMounted] = useState(isControlled && controlledVal && !!resolvedContent && !isDisabled);
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ x: -9999, y: -9999 });
    const [actualP, setActualP] = useState<TooltipPlacement>(placement);
    // How far along the cross-axis the arrow sits (in px from panel start)
    const [arrowAlong, setArrowAlong] = useState(12);

    // ── Timers ────────────────────────────────────────────────────────────────
    const tShow = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tHide = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tUnmount = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearAll = useCallback(() => {
        if (tShow.current) clearTimeout(tShow.current);
        if (tHide.current) clearTimeout(tHide.current);
        if (tUnmount.current) clearTimeout(tUnmount.current);
    }, []);

    // ── Show / hide helpers ───────────────────────────────────────────────────
    const doMount = useCallback(() => {
        clearAll();
        setMounted(true);
        onOpenChange?.(true);
    }, [clearAll, onOpenChange]);

    const doHide = useCallback(() => {
        clearAll();
        setVisible(false);
        onOpenChange?.(false);
        tUnmount.current = setTimeout(() => setMounted(false), 180);
    }, [clearAll, onOpenChange]);

    const triggerShow = useCallback(() => {
        clearAll();
        if (delayShow > 0) tShow.current = setTimeout(doMount, delayShow);
        else doMount();
    }, [clearAll, delayShow, doMount]);

    const triggerHide = useCallback(() => {
        clearAll();
        if (delayHide > 0) tHide.current = setTimeout(doHide, delayHide);
        else doHide();
    }, [clearAll, delayHide, doHide]);

    // ── Controlled sync ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!isControlled) return;
        clearAll();
        if (controlledVal && resolvedContent && !isDisabled) {
            setMounted(true);
            onOpenChange?.(true);
        } else {
            setVisible(false);
            onOpenChange?.(false);
            tUnmount.current = setTimeout(() => setMounted(false), 180);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isControlled, controlledVal, isDisabled]);

    // ── Cleanup on unmount ────────────────────────────────────────────────────
    useEffect(() => () => clearAll(), [clearAll]);

    // ── Position + fade in once mounted ──────────────────────────────────────
    useLayoutEffect(() => {
        if (!mounted || !tipRef.current || !anchorRef.current) return;

        const tip = tipRef.current;
        const anchor = anchorRef.current.getBoundingClientRect();
        const tw = tip.offsetWidth;
        const th = tip.offsetHeight;
        if (!tw && !th) return;

        const { x, y, placement: ap } = calcPosition(anchor, tw, th, placement, offset);
        setPos({ x, y });
        setActualP(ap);

        // Compute arrow offset along the cross-axis so it always points at anchor centre
        const { base } = parsePlacement(ap);
        const ARROW_HALF = 4; // half of 8px arrow square
        if (base === 'top' || base === 'bottom') {
            const anchorCX = anchor.left + anchor.width / 2;
            setArrowAlong(Math.max(8, Math.min(Math.round(anchorCX - x - ARROW_HALF), tw - 18)));
        } else {
            const anchorCY = anchor.top + anchor.height / 2;
            setArrowAlong(Math.max(8, Math.min(Math.round(anchorCY - y - ARROW_HALF), th - 18)));
        }

        // Double rAF: let the browser paint opacity:0 before transitioning in
        requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    }, [mounted]); // intentionally omits placement/offset — they are stable per mount

    // ── Reposition on scroll / resize ────────────────────────────────────────
    useEffect(() => {
        if (!mounted) return;
        const reposition = () => {
            if (!tipRef.current || !anchorRef.current) return;
            const tip = tipRef.current;
            const anchor = anchorRef.current.getBoundingClientRect();
            const { x, y, placement: ap } = calcPosition(anchor, tip.offsetWidth, tip.offsetHeight, placement, offset);
            setPos({ x, y });
            setActualP(ap);
        };
        window.addEventListener('scroll', reposition, { passive: true, capture: true });
        window.addEventListener('resize', reposition, { passive: true });
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [mounted, placement, offset]);

    // ── Ref injection callback ────────────────────────────────────────────────
    const refHandler = useCallback(
        (el: Element | null) => {
            anchorRef.current = el;
            // Forward to the parent if this Tooltip is itself nested inside another
            if (typeof forwardedRef === 'function') forwardedRef(el);
            else if (forwardedRef) (forwardedRef as React.MutableRefObject<Element | null>).current = el;
            // Also forward any existing ref the child had
            const existingRef = isValidElement(children) ? (children as any).ref : null;
            if (typeof existingRef === 'function') existingRef(el);
            else if (existingRef && 'current' in existingRef) existingRef.current = el;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [forwardedRef],
    );

    // ── Build the trigger element ─────────────────────────────────────────────
    const child = isValidElement(children) ? children : <span>{children}</span>;

    // Only attach hover/focus handlers in uncontrolled mode
    const hoverHandlers =
        !isControlled && !isDisabled && resolvedContent
            ? {
                onMouseEnter: (e: React.MouseEvent) => { triggerShow(); (child.props as any).onMouseEnter?.(e); },
                onMouseLeave: (e: React.MouseEvent) => { triggerHide(); (child.props as any).onMouseLeave?.(e); },
                onFocus: (e: React.FocusEvent) => { triggerShow(); (child.props as any).onFocus?.(e); },
                onBlur: (e: React.FocusEvent) => { triggerHide(); (child.props as any).onBlur?.(e); },
            }
            : {};

    const trigger = cloneElement(child as React.ReactElement<any>, {
        ref: refHandler,
        ...hoverHandlers,
    });

    // ── Early exit — no content or disabled ──────────────────────────────────
    if (!resolvedContent || isDisabled) return <>{trigger}</>;

    // ── Panel class ───────────────────────────────────────────────────────────
    const panelClass = [
        // Base structural classes — always applied
        'relative rounded-xl text-[11.5px] font-[450] tracking-[0.012em] leading-[1.45] select-none whitespace-pre-wrap',
        // Variant paint + spacing (skipped when unstyled)
        !unstyled && 'px-[10px] py-[6px]',
        !unstyled && PANEL_CLS[variant],
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const { base } = parsePlacement(actualP);

    // ── Panel inline style ────────────────────────────────────────────────────
    //
    // Enter: spring cubic-bezier (0.34,1.56,0.64,1) gives a subtle overshoot
    // that reads as confident and snappy rather than mechanical.
    // Exit:  faster ease-in so it doesn't linger.
    const enterTransition =
        'opacity 140ms ease-out, transform 160ms cubic-bezier(0.34,1.56,0.64,1)';
    const exitTransition =
        'opacity 110ms ease-in, transform 110ms ease-in';

    const panelStyle: React.CSSProperties = {
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        zIndex: 9999,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transform: visible
            ? 'scale(1) translateX(0) translateY(0)'
            : `scale(0.88) ${ANIM_EXIT_SHIFT[base]}`,
        transition: visible ? enterTransition : exitTransition,
        transformOrigin: ANIM_ORIGIN[base],
        // Hardware-accelerated compositing layer
        willChange: 'transform, opacity',
    };

    // ── Arrow ─────────────────────────────────────────────────────────────────
    // 8×8 rotated square — two border sides exposed to match panel edge.
    // Offset by -4 (half of 8) to sit flush at panel edge.
    const arrowPositionStyle: React.CSSProperties =
        base === 'top' ? { bottom: -4, left: arrowAlong } :
            base === 'bottom' ? { top: -4, left: arrowAlong } :
                base === 'left' ? { right: -4, top: arrowAlong } :
                    { left: -4, top: arrowAlong };

    const arrowClass = [
        'absolute w-[8px] h-[8px] rotate-45',
        !unstyled && ARROW_BG[variant],
        !unstyled && ARROW_BORDER[variant],
        ARROW_SIDES[base],
    ]
        .filter(Boolean)
        .join(' ');

    // ── Render ────────────────────────────────────────────────────────────────
    // Plain strings are wrapped in <Text> so typography is consistent with
    // the rest of the design system; ReactNode content renders as-is.
    // When `unstyled` is true the panel paint is fully custom — use `inherit`
    // so the caller's className text-colour applies instead of forcing white.
    const renderedContent =
        typeof resolvedContent === 'string'
            ? (
                <Text as="span" size="base" color={(unstyled || variant === 'light') ? 'inherit' : 'white'} leading="snug">
                    {resolvedContent}
                </Text>
            )
            : resolvedContent;

    return (
        <>
            {trigger}
            {mounted &&
                createPortal(
                    <div
                        ref={tipRef}
                        role="tooltip"
                        style={panelStyle}
                        className={panelClass}
                    >
                        {renderedContent}
                        {arrow && <div className={arrowClass} style={arrowPositionStyle} />}
                    </div>,
                    document.body,
                )}
        </>
    );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;
